import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import useFormatter from '../../common/useFormatter';
import { ColumnConfig } from '../../components/DatasetTable';
// Removed invalid import - using direct color names instead
import { useRecoilValue } from 'recoil';
import EmptyValueTag from '../../simulation/tags/EmptyValueTag';
import useActualityAggregationFieldDescriptors from '../hooks/useActualityAggregationFieldDescriptors';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import { actualityAggregatedStatsGroupBy } from '../store/actuality.kpi.state';
import { KpiGroupRow } from '../store/actuality.types';

function useActualityKpiMetricsTableConfig(): ColumnConfig<KpiGroupRow>[] {
  const { t } = useTranslation('dataset');
  const formatter = useFormatter();
  const groupBy = useRecoilValue(actualityAggregatedStatsGroupBy);
  const fieldDescriptors = useActualityAggregationFieldDescriptors();
  const metricDescriptors = useActualityAggregationMetricDescriptors();

  const groupFieldDescriptor = fieldDescriptors[groupBy];

  // Start with the group column
  const columnsConfig: ColumnConfig<KpiGroupRow>[] = [
    {
      field: 'groupValue',
      title: groupFieldDescriptor?.title || groupBy,
      hasFilter: true,
      hasSort: true,
      render: (groupValue: string, row) => {
        // Use field descriptor formatter if available
        const formattedValue = groupFieldDescriptor?.formatter
          ? groupFieldDescriptor.formatter(groupValue)
          : groupValue;

        return (
          <div className="flex flex-col">
            <span className="font-medium">{formattedValue || groupValue}</span>
            {/* <span className="text-menu-text/70 text-xs">{row.groupLabel}</span> */}
          </div>
        );
      },
    },
  ];

  // Add dynamic columns for each metric
  Object.entries(metricDescriptors).forEach(([metricId, descriptor]) => {
    columnsConfig.push({
      field: metricId,
      title: descriptor.legend || descriptor.title,
      hasSort: true,
      render: (value: number) => {
        if (_.isNil(value) || value === 0) {
          return <EmptyValueTag />;
        }

        const formatted = descriptor.getFormatted
          ? descriptor.getFormatted(value)
          : { value: value.toString(), unit: '' };

        return (
          <div className="bg-menu/10 flex items-baseline gap-1 text-right">
            <span className="font-mono font-semibold">{formatted.value}</span>
            {formatted.unit && (
              <span className="text-menu-text/70 text-xs">
                {formatted.unit}
              </span>
            )}
          </div>
        );
      },
    });
  });

  return columnsConfig;
}

export default useActualityKpiMetricsTableConfig;
